import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Download, Share, X, Plus, Check } from 'lucide-react';

const VISIT_COUNT_KEY = 'drh_visit_count';
const BANNER_DISMISSED_KEY = 'drh_install_banner_dismissed';

/**
 * Shared hook: manages beforeinstallprompt capture, install triggering,
 * iOS detection, installed state, and visit count tracking.
 */
export function useInstallApp() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [justInstalled, setJustInstalled] = useState(false);

  const isIos = typeof window !== 'undefined' && /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone
  );

  useEffect(() => {
    if (isStandalone) { setInstalled(true); return; }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setJustInstalled(true);
      setDeferredPrompt(null);
      setTimeout(() => setJustInstalled(false), 4000);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [isStandalone]);

  const triggerInstall = useCallback(async () => {
    if (!deferredPrompt) return { ok: false, needsHelp: true };
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (outcome === 'accepted') {
        setJustInstalled(true);
        setTimeout(() => setJustInstalled(false), 4000);
        return { ok: true, needsHelp: false };
      }
      // User dismissed — still count as 'handled' (no fallback modal)
      return { ok: false, needsHelp: false };
    } catch (err) {
      // Stale/used event or unsupported → show help modal
      console.warn('Native install prompt failed, falling back to help modal:', err);
      setDeferredPrompt(null);
      return { ok: false, needsHelp: true };
    }
  }, [deferredPrompt]);

  return { triggerInstall, installed, justInstalled, isIos, canPrompt: !!deferredPrompt };
}

/**
 * PWA Install button.
 */
export default function InstallAppButton({ variant = 'primary' }) {
  const { triggerInstall, installed, justInstalled, isIos, canPrompt } = useInstallApp();
  const [showHelp, setShowHelp] = useState(false);

  const handleClick = useCallback(async () => {
    if (isIos) { setShowHelp(true); return; }
    if (canPrompt) {
      const { needsHelp } = await triggerInstall();
      if (needsHelp) setShowHelp(true);
      return;
    }
    // No native prompt available — show instructions
    setShowHelp(true);
  }, [canPrompt, isIos, triggerInstall]);

  // Already installed → show a subtle "installed" chip
  if (installed) {
    return (
      <span
        data-testid="install-app-installed"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold"
        style={{ backgroundColor: '#22C55E20', color: '#22C55E', fontFamily: 'Manrope' }}
        title="This app is installed on your device"
      >
        <Check className="w-3.5 h-3.5" /> App Installed
      </span>
    );
  }

  const baseStyle = variant === 'primary'
    ? { backgroundColor: '#FF6F59', color: 'white', boxShadow: '0 4px 15px rgba(255,111,89,0.35)' }
    : { backgroundColor: 'rgba(255,255,255,0.12)', color: '#A3D4D4' };

  return (
    <>
      <button
        data-testid="install-app-btn"
        onClick={handleClick}
        className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-[11px] sm:text-xs font-bold transition-all hover:-translate-y-0.5"
        style={{ ...baseStyle, fontFamily: 'Manrope, sans-serif' }}
      >
        <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        <span className="hidden sm:inline">Add App</span>
        <span className="sm:hidden">Install</span>
      </button>

      {justInstalled && typeof document !== 'undefined' && createPortal(
        <div
          data-testid="install-success-toast"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10001] px-5 py-3 rounded-full text-sm font-bold flex items-center gap-2 animate-fade-slide-up"
          style={{ backgroundColor: '#22C55E', color: 'white', boxShadow: '0 6px 20px rgba(34,197,94,0.4)', fontFamily: 'Manrope' }}
        >
          <Check className="w-4 h-4" /> App added to your device!
        </div>,
        document.body
      )}

      {showHelp && <InstallHelpModal isIos={isIos} onClose={() => setShowHelp(false)} />}
    </>
  );
}

/**
 * Shared install-help modal with step-by-step instructions.
 * Exported so InstallBanner can reuse it.
 */
export function InstallHelpModal({ isIos, onClose }) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="fixed inset-0 overflow-y-auto"
      style={{ zIndex: 10002, backgroundColor: 'rgba(0,0,0,0.8)' }}
      onClick={onClose}
      data-testid="install-help-overlay"
    >
      <div className="min-h-full w-full flex items-start sm:items-center justify-center p-4 py-10">
        <div
          className="w-full max-w-md rounded-2xl p-5 sm:p-6 relative shadow-2xl"
          style={{ backgroundColor: '#F4F1EB' }}
          onClick={(e) => e.stopPropagation()}
        >
        <button
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#EAE5D9', color: '#4A5D5D' }}
          onClick={onClose}
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-xl md:text-2xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
          {isIos ? 'Add to your iPhone' : 'Install the App'}
        </h3>
        <p className="text-xs mb-5" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
          Tap the app button on your home screen anytime to jump straight back in — no typing URLs.
        </p>

        {isIos ? (
          <ol className="space-y-3">
            <Step n={1} title="Tap the Share button" sub="at the bottom of Safari" icon={<Share className="w-3.5 h-3.5 inline" />} />
            <Step n={2} title={'Choose "Add to Home Screen"'} sub="Scroll down if needed" icon={<Plus className="w-3.5 h-3.5 inline" />} />
            <Step n={3} title={'Tap "Add"'} sub="The app icon will appear on your home screen" />
          </ol>
        ) : (
          <ol className="space-y-3">
            <Step n={1} title="Look at your address bar" sub="Chrome & Edge show an install icon on the right side of the URL." />
            <Step n={2} title="Click the install icon" sub={'Or open the browser menu (⋮) → "Install app".'} />
            <Step n={3} title={'Confirm "Install"'} sub="The app will open in its own window and a shortcut goes to your desktop or app drawer." />
          </ol>
        )}

        <button
          onClick={onClose}
          className="w-full mt-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:-translate-y-0.5"
          style={{ backgroundColor: '#1F4FBE', fontFamily: 'Manrope' }}
        >
          Got it
        </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Step({ n, title, sub, icon }) {
  return (
    <li className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: '#EAE5D9' }}>
      <span className="flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0" style={{ backgroundColor: '#1F4FBE', color: 'white', fontFamily: 'Manrope' }}>{n}</span>
      <div className="flex-1">
        <p className="text-sm font-bold" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>{title}</p>
        <p className="text-xs flex items-center gap-1.5" style={{ fontFamily: 'Manrope', color: '#4A5D5D' }}>
          {icon}{sub}
        </p>
      </div>
    </li>
  );
}

/**
 * One-time "Welcome back" install banner.
 * Shown at the bottom of the screen on the 2nd+ visit, once.
 * Auto-dismisses after 12 seconds. Persists dismissal to localStorage.
 */
export function InstallBanner() {
  const { triggerInstall, installed, isIos, canPrompt } = useInstallApp();
  const [visible, setVisible] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Increment visit count on mount
    const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10) + 1;
    localStorage.setItem(VISIT_COUNT_KEY, String(count));

    const dismissed = localStorage.getItem(BANNER_DISMISSED_KEY) === '1';
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (dismissed || standalone) return;

    // Show only on 2nd visit (or later, if never shown and never dismissed)
    if (count >= 2) {
      const t = setTimeout(() => setVisible(true), 1500); // subtle delay for polish
      return () => clearTimeout(t);
    }
  }, []);

  // Auto-dismiss after 12s
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => dismiss(), 12000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = useCallback(() => {
    setVisible(false);
    // Intentional no-op on failure: localStorage.setItem can throw in Safari
    // private-browsing mode or when storage is full. Failing to persist "dismissed"
    // is non-critical — worst case the banner shows once more on the next visit.
    try { localStorage.setItem(BANNER_DISMISSED_KEY, '1'); } catch { /* storage unavailable — banner will reappear next visit */ }
  }, []);

  const handleInstall = useCallback(async () => {
    if (isIos || !canPrompt) { setShowHelp(true); return; }
    const { ok, needsHelp } = await triggerInstall();
    if (ok) dismiss();
    else if (needsHelp) setShowHelp(true);
  }, [canPrompt, isIos, triggerInstall, dismiss]);

  if (installed) return null;

  return (
    <>
      {visible && (
        <div
          data-testid="install-banner"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[10000] w-[92vw] max-w-md rounded-2xl p-4 shadow-2xl animate-fade-slide-up"
          style={{ backgroundColor: '#1A2E2E', border: '1px solid rgba(163,212,212,0.2)' }}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FF6F59' }}>
              <Download className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight" style={{ fontFamily: 'Manrope', color: '#F4F1EB' }}>
                Welcome back!
              </p>
              <p className="text-xs mt-0.5 leading-relaxed" style={{ fontFamily: 'Manrope', color: '#A3D4D4' }}>
                Add this to your home screen for quick access anytime.
              </p>
            </div>
            <button
              data-testid="install-banner-dismiss"
              onClick={dismiss}
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors"
              style={{ backgroundColor: 'rgba(163,212,212,0.1)', color: '#A3D4D4' }}
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              data-testid="install-banner-cta"
              onClick={handleInstall}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: '#FF6F59', fontFamily: 'Manrope', boxShadow: '0 4px 12px rgba(255,111,89,0.3)' }}
            >
              <Download className="w-3.5 h-3.5" /> Install App
            </button>
            <button
              data-testid="install-banner-later"
              onClick={dismiss}
              className="px-4 py-2.5 rounded-xl text-xs font-bold transition-colors"
              style={{ backgroundColor: 'rgba(163,212,212,0.1)', color: '#A3D4D4', fontFamily: 'Manrope' }}
            >
              Not now
            </button>
          </div>
        </div>
      )}
      {showHelp && <InstallHelpModal isIos={isIos} onClose={() => setShowHelp(false)} />}
    </>
  );
}
